/**
 * The two date windows, drawn on one axis.
 *
 * Replaces the Timeframe row, which gave each window its own cell and could therefore
 * say when each one runs and never how they sit together. Whether validation picks up
 * where training stops, whether months are missing between them, whether they overlap:
 * all of it is one glance here and unreadable from two cells.
 *
 * Geometry comes from `datasetTimeline`, which is tested and does not judge what it
 * computes. This draws it and does not judge it either: an overlap is drawn accurately
 * and left alone, because whether two windows should overlap is a modelling question,
 * not a rendering one.
 */
// Named TimelineBands, not DatasetTimeline, because `datasetTimeline.ts` sits beside it
// and macOS resolves the two as one file: importing the component silently got the
// geometry module's interface instead, and the error it produced ("only refers to a
// type") named neither file.
import React from 'react';

import { DatasetTimeline as Timeline } from './datasetTimeline';
import './datasetTimeline.css';

export function TimelineBands({ timeline }: { timeline: Timeline }) {
  const band = (
    kind: 'training' | 'validation',
    name: string,
    { leftPct, widthPct, label, months }: Timeline['training']
  ) => (
    <div className="dtl-row">
      <div className="dtl-track">
        <div
          className={`dtl-band dtl-band--${kind}`}
          style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
        />
      </div>
      <div className="dtl-legend">
        <span className={`dtl-key dtl-key--${kind}`} aria-hidden />
        <span className="dtl-name">{name}</span>
        <span className="dtl-range">{label}</span>
        <span className="dtl-months">
          {months} month{months === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  );

  return (
    <div className="dtl">
      <div className="dtl-axis" aria-hidden>
        <span>{timeline.from}</span>
        <span>{timeline.to}</span>
      </div>
      {band('training', 'Training', timeline.training)}
      {band('validation', 'Validation', timeline.validation)}
    </div>
  );
}

export default TimelineBands;
